import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WestRoundedIcon from "@mui/icons-material/WestRounded";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { assetKeys, entityKeys } from "../../../../../api/queryKeys";
import ContainedButton from "../../../../../components/buttons/ContainedButton";
import TextButton from "../../../../../components/buttons/TextButton";
import CustomTooltip from "../../../../../components/CustomTooltip";
import { Toast } from "../../../../../components/Toast";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import {
  AssetClass,
  ComplexEntityTypes,
  StepsAssetWizard,
} from "../../../../../utils/constants";
import { useEntities } from "../../../environment/hooks/useEntities";
import AssetDetailsStep from "./AssetDetailsStep";
import ManualAddStep from "./ManualAddStep";

const defaultFormData = {
  stepAssetDetails: {
    asset: {
      asset_id: "",
      assetName: "",
      assetType: "",
      reason: "",
      businessValue: "",
    },
  },

  stepManualAdd: { entities: [], entitiesSelected: [] },
};

const CreateNewAssetModal = ({ open, onClose, assetToEdit = null, steps }) => {
  const [formData, setFormData] = useState(defaultFormData);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const { useQueriesByTypes } = useEntities();
  const { businessValueMode } = useContext(EnvFeatureFlagContext);
  let { entitiesAreLoading: fetchedEntitiesAreLoading, entities: allEntities } =
    useQueriesByTypes({ types: Object.values(ComplexEntityTypes) });

  allEntities = allEntities ?? [];

  const { createAsset, trackEvent, updateAsset, refreshEntitiesOverview } =
    useAPI();

  const getIndexValueFromSteps = (step) => {
    return steps?.indexOf(step);
  };
  const [activeStep, setActiveStep] = React.useState(
    assetToEdit
      ? getIndexValueFromSteps(StepsAssetWizard.ManualAddStep)
      : getIndexValueFromSteps(StepsAssetWizard.AssetDetailsStep)
  );
  const colors = useColors();
  const queryClient = useQueryClient();

  const findFullEntities = (entity_ids) => {
    let entities = allEntities.filter((entity) => {
      return entity_ids.includes(entity?.entity_id);
    });
    return entities.map((entity) => {
      return {
        ...entity,
        id: entity.entity_id,
      };
    });
  };

  useEffect(() => {
    if (assetToEdit?.asset && assetToEdit && !fetchedEntitiesAreLoading) {
      let entities = findFullEntities(assetToEdit?.asset?.entity_ids);

      setFormData({
        stepAssetDetails: {
          asset: {
            asset_id: assetToEdit.asset.asset_id,
            assetName: assetToEdit.asset.name,
            assetType: assetToEdit.asset.type,
            reason: assetToEdit.asset.reason ? assetToEdit.asset.reason : "",
            businessValue: assetToEdit.asset.business_value,
          },
        },
        stepManualAdd: {
          entities: [...entities],
          entitiesSelected: [],
        },
      });
    }
  }, [assetToEdit?.asset, fetchedEntitiesAreLoading]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCloseModal = () => {
    onClose();
  };
  const handleAssetUpdate = async (assetData) => {
    trackEvent(`User is updating asset named ${assetData.name}`).then();
    setConfirmationLoading(true);
    await updateAsset(assetData)
      .then(() => {
        Toast.success(`Asset ${assetData.name} updated successfully!`);
        queryClient.invalidateQueries({ queryKey: assetKeys.all });
        refreshEntitiesOverview().then(() => {
          queryClient.invalidateQueries({
            queryKey: entityKeys.entitiesOverview(),
          });
        });
        setConfirmationLoading(false);

        onClose();
      })
      .catch((error) => {
        setConfirmationLoading(false);

        Toast.error("An error occurred while updating the asset.");
      });
  };
  const handleAssetCreation = async (assetData) => {
    trackEvent(`User is creating a new asset named ${assetData.name}`).then();
    setConfirmationLoading(true);
    await createAsset(assetData)
      .then(() => {
        Toast.success({
          text: (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "350px",
              }}
            >
              <Typography variant="subtitle2">
                Asset {assetData.name} created successfully!
              </Typography>
              <Typography variant="body2" color={colors.text_secondary}>
                Risk data for this asset will update after the next scan.
              </Typography>
            </Box>
          ),
        });

        queryClient.invalidateQueries({ queryKey: assetKeys.all });
        refreshEntitiesOverview().then(() => {
          queryClient.invalidateQueries({
            queryKey: entityKeys.entitiesOverview(),
          });
        });

        setConfirmationLoading(false);
        setFormData(defaultFormData);
        setActiveStep(0);
        handleCloseModal();
      })
      .catch(() => {
        setConfirmationLoading(false);
        Toast.error("An error occurred while creating the asset.");
      });
  };

  const handleAssetConfirmation = async () => {
    const entity_ids = formData.stepManualAdd.entities.map(
      (entity) => entity.entity_id
    );

    const assetData = {
      asset_id: formData.stepAssetDetails.asset.asset_id,
      name: formData.stepAssetDetails.asset.assetName,
      type: formData.stepAssetDetails.asset.assetType,
      reason: formData.stepAssetDetails.asset.reason,
      business_value: formData.stepAssetDetails.asset.businessValue,
      entity_ids: entity_ids,
    };
    if (!assetToEdit) {
      handleAssetCreation(assetData);
    } else {
      handleAssetUpdate(assetData);
    }
  };

  const isEditSmartAsset =
    assetToEdit && assetToEdit.asset.cls === AssetClass.SMART;

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      fullWidth={true}
      PaperProps={{
        sx: {
          width: "900px",
          maxWidth: "900px",
          minHeight: "95vh",
          height: "95vh",
          margin: 0,
        },
      }}
      sx={{
        zIndex: 800,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflow: "hidden",
          height: "100%",
          padding: 4,
          background: colors.create_and_update_wizard_bg,
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Box display={"flex"} flexDirection={"row"} gap={1}>
            {isEditSmartAsset ? (
              <Box
                display={"flex"}
                flexDirection={"row"}
                gap={1}
                alignItems={"center"}
              >
                <Typography variant="subtitle1">
                  Smart Critical Asset
                </Typography>
                <CustomTooltip
                  title={
                    "Breeze created this asset based on predefined logic. Only the Business Value is editable."
                  }
                  placement="top"
                >
                  <InfoRoundedIcon
                    sx={{
                      width: "16px",
                      height: "16px",
                      color: colors.grey_500,
                    }}
                  />
                </CustomTooltip>
              </Box>
            ) : (
              <>
                <Typography variant="body2" color={colors.text_secondary}>
                  Step {activeStep + 1} of {steps.length}
                </Typography>
                <Typography variant="body2" color={colors.action_active}>
                  {steps[activeStep].label}
                </Typography>
              </>
            )}
          </Box>

          <CloseRoundedIcon
            onClick={handleCloseModal}
            sx={{
              cursor: "pointer",
              width: "18px",
              height: "18px",
              color: colors.text_secondary,
            }}
          />
        </Box>

        {!isEditSmartAsset && (
          <Typography variant="subtitle1">Create Critical Asset</Typography>
        )}
        {
          <DialogContent
            style={{ overflowY: "auto", width: "100%", padding: "8px 0" }}
          >
            {activeStep ===
              getIndexValueFromSteps(StepsAssetWizard.AssetDetailsStep) && (
              <AssetDetailsStep
                setFormData={setFormData}
                formData={formData}
                assetToEdit={assetToEdit}
              />
            )}

            {activeStep ===
              getIndexValueFromSteps(StepsAssetWizard.ManualAddStep) && (
              <ManualAddStep
                setFormData={setFormData}
                formData={formData}
                allEntities={allEntities}
                fetchedEntitiesAreLoading={fetchedEntitiesAreLoading}
                assetToEdit={assetToEdit}
              />
            )}
          </DialogContent>
        }
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            gap: 2,
          }}
        >
          {activeStep === 0 || isEditSmartAsset ? (
            <TextButton
              onClick={handleCloseModal}
              color="inherit"
              size="medium"
              style={{ textTransform: "none" }}
            >
              <Typography variant="overline" style={{ textTransform: "none" }}>
                Cancel
              </Typography>
            </TextButton>
          ) : (
            <TextButton
              onClick={handleBack}
              color="primary"
              size="medium"
              style={{ textTransform: "none" }}
              disabled={
                activeStep ===
                getIndexValueFromSteps(StepsAssetWizard.AssetDetailsStep)
              } // disable the Back button on the first step
            >
              <Box display={"flex"} flexDirection={"row"} gap={1}>
                <WestRoundedIcon sx={{ width: "18px", height: "18px" }} />
                <Typography
                  variant="overline"
                  style={{ textTransform: "none" }}
                >
                  Back
                </Typography>
              </Box>
            </TextButton>
          )}
          {activeStep ===
          getIndexValueFromSteps(StepsAssetWizard.ManualAddStep) ? (
            <>
              {!assetToEdit ? (
                <ContainedButton
                  onClick={handleAssetConfirmation}
                  color="primary"
                  size="medium"
                  disabled={
                    formData.stepAssetDetails.asset.assetName === "" ||
                    formData.stepAssetDetails.asset.assetType === "" ||
                    ((formData.stepAssetDetails.asset.businessValue === "" ||
                      formData.stepAssetDetails.asset.businessValue === null ||
                      formData.stepAssetDetails.asset.businessValue ===
                        undefined) &&
                      businessValueMode) ||
                    formData.stepManualAdd.entitiesSelected?.length === 0 ||
                    confirmationLoading
                  }
                >
                  <Box gap={1} display={"flex"} flexDirection={"row"}>
                    <Typography
                      variant="overline"
                      style={{ textTransform: "none" }}
                    >
                      Create Asset
                    </Typography>
                    {confirmationLoading && <CircularProgress size="1rem" />}
                  </Box>
                </ContainedButton>
              ) : (
                <ContainedButton
                  onClick={handleAssetConfirmation}
                  color="primary"
                  size="medium"
                  disabled={
                    formData.stepAssetDetails.asset.assetName === "" ||
                    formData.stepAssetDetails.asset.assetType === "" ||
                    ((formData.stepAssetDetails.asset.businessValue === "" ||
                      formData.stepAssetDetails.asset.businessValue === null ||
                      formData.stepAssetDetails.asset.businessValue ===
                        undefined) &&
                      businessValueMode) ||
                    formData.stepManualAdd.entities?.length === 0 ||
                    confirmationLoading ||
                    (formData.stepAssetDetails.asset.assetName ===
                      assetToEdit.asset.name &&
                      formData.stepAssetDetails.asset.assetType ===
                        assetToEdit.asset.type &&
                      formData.stepAssetDetails.asset.reason ===
                        (assetToEdit.asset.reason || "") &&
                      formData.stepAssetDetails.asset.businessValue ===
                        assetToEdit.asset.business_value &&
                      JSON.stringify(
                        formData.stepManualAdd.entities
                          .map((e) => e.entity_id)
                          .sort()
                      ) === JSON.stringify(assetToEdit.asset.entity_ids.sort()))
                  }
                >
                  <Box gap={1} display={"flex"} flexDirection={"row"}>
                    <Typography
                      variant="overline"
                      style={{ textTransform: "none" }}
                    >
                      Save Changes
                    </Typography>
                    {confirmationLoading && <CircularProgress size="1rem" />}
                  </Box>
                </ContainedButton>
              )}
            </>
          ) : (
            <TextButton
              onClick={handleNext}
              color="primary"
              size="medium"
              disabled={
                activeStep ===
                  getIndexValueFromSteps(StepsAssetWizard.AssetDetailsStep) &&
                (formData.stepAssetDetails.asset.assetName === "" ||
                  formData.stepAssetDetails.asset.assetType === "" ||
                  ((formData.stepAssetDetails.asset.businessValue === "" ||
                    formData.stepAssetDetails.asset.businessValue === null ||
                    formData.stepAssetDetails.asset.businessValue ===
                      undefined) &&
                    businessValueMode))
              } // disable the Next button on the second step
            >
              <Box
                display={"flex"}
                flexDirection={"row"}
                gap={1}
                alignItems={"center"}
              >
                <Typography
                  variant="overline"
                  style={{ textTransform: "none" }}
                >
                  Next
                </Typography>
                <EastRoundedIcon sx={{ width: "16px", height: "16px" }} />
              </Box>
            </TextButton>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateNewAssetModal;
